<template>
	<a-modal
		:visible="visible"
		:footer="null"
		:closable="false"
		:mask-closable="false"
		:destroy-on-close="true"
		width="640px"
		:centered="true"
		wrap-class-name="g-file-settings-modal"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-file-settings">
			<div class="g-file-settings__header">
				<span class="g-file-settings__title">文件模块</span>
				<span class="g-file-settings__close mbm-iconfont mbm-guanbi1" @click="onCancel"></span>
			</div>

			<UploadProgressPanel
				v-if="showFullUploadPanel"
				:tasks="uploadTasks"
				:merge="uploadTasks.length > 1"
				variant="modal"
			/>
			<EditListSortGroup
				v-else
				ref="sortList"
				class="g-file-settings__list"
				@drop="onSortDrop"
			>
				<div
					v-for="(item, index) in form.items"
					:key="item.id"
					:data-sort-id="item.id"
					class="g-file-settings__item"
					:class="[
						{ 'is-uploading': isActiveUploadItem(item, index) },
						dragId === item.id ? 'is-list-sort-dragging' : '',
					]"
					:draggable="!item.uploading"
					@dragstart="onDragStart(item.id)"
					@dragover.prevent="onItemDragOver(item.id, $event)"
					@dragend="onDragEnd"
				>
					<template v-if="isActiveUploadItem(item, index)">
						<UploadProgressPanel
							:tasks="uploadTasks"
							:merge="uploadTasks.length > 1"
							variant="row"
						/>
					</template>
					<template v-else>
						<div class="g-file-settings__icon" aria-hidden="true">
							<img :src="getFileIconUrl(item)" class="g-file-settings__icon-img" :alt="getFileIconMark(item)" />
						</div>
						<div class="g-file-settings__info">
							<div class="g-file-settings__name">{{ item.filename }}</div>
							<div class="g-file-settings__size">{{ item.filesize }}</div>
						</div>
						<div class="g-file-settings__actions">
							<span
								class="g-file-settings__action-btn mbm-iconfont mbm-tihuan3"
								title="更换"
								@click="onReplaceFile(index)"
							></span>
							<span
								class="g-file-settings__action-btn is-delete mbm-iconfont mbm-guanbi1"
								title="删除"
								@click="onDeleteFile(index)"
							></span>
						</div>
					</template>
				</div>
			</EditListSortGroup>

			<div class="g-file-settings__toolbar">
				<span class="g-file-settings__hint">文件支持拖拽排序</span>
				<div class="g-file-settings__toolbar-main">
					<div class="g-file-settings__toolbar-left">
						<button
							type="button"
							class="g-file-settings__outline-btn"
							:disabled="isUploading"
							@click="onAddFiles"
						>
							继续上传
						</button>
					</div>
					<button
						type="button"
						class="g-file-settings__outline-btn"
						:disabled="isUploading"
						@click="downloadSettingsVisible = true"
					>
						下载设置
					</button>
				</div>
			</div>

			<div class="g-file-settings__footer">
				<span class="g-file-settings__count">已上传 {{ uploadedItems.length }} 个文件</span>
				<div class="g-file-settings__footer-actions">
					<button type="button" class="g-file-settings__btn is-cancel" @click="onCancel">取消</button>
					<button
						type="button"
						class="g-file-settings__btn is-confirm"
						:disabled="!uploadedItems.length || isUploading"
						@click="onConfirm"
					>确认</button>
				</div>
			</div>
		</div>
		</div>

		<input
			ref="fileInput"
			type="file"
			multiple
			class="g-file-settings__file-input"
			@change="onFileChange"
		/>
		<input
			ref="replaceInput"
			type="file"
			class="g-file-settings__file-input"
			@change="onReplaceFileChange"
		/>

		<FileDownloadSettingsModal
			v-model:visible="downloadSettingsVisible"
			:value="form.downloadSettings"
			@confirm="onDownloadSettingsConfirm"
		/>
	</a-modal>
</template>

<script>
	import { v4 as uuidv4 } from 'uuid';
	import {
		MAX_LOCAL_FILES,
		createDefaultFileDownloadSettings,
		filterValidLocalFileList,
		normalizeHeaderFiles,
		normalizeLocalFiles,
		revokeLocalFileItem,
		revokeLocalFiles,
	} from '../utils/localFile.js';
	import { LOCAL_ASSET } from '../utils/localAssets.js';
	import {
		getPayFileUnlockFileIconKey,
		getPayFileUnlockFileIconMark,
	} from '../utils/payFileUnlock.js';
	import FileDownloadSettingsModal from './FileDownloadSettingsModal.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import EditListSortGroup from './EditListSortGroup.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GFileSettingsModal',
		components: { EditListSortGroup, FileDownloadSettingsModal, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			value: {
				type: Object,
				default: () => ({
					items: [],
				}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				maxFiles: MAX_LOCAL_FILES,
				downloadSettingsVisible: false,
				form: {
					items: [],
					downloadSettings: createDefaultFileDownloadSettings(),
				},
				dragId: '',
				dragOverId: '',
				replaceIndex: -1,
				skipNextInit: false,
				isUploading: false,
				uploadMode: 'initial',
				uploadTargetId: '',
				replaceUploadIndex: -1,
				uploadTasks: [],
			};
		},
		computed: {
			showFullUploadPanel() {
				return this.isUploading && this.uploadMode === 'initial';
			},
			uploadedItems() {
				return this.form.items.filter((item) => !item.uploading);
			},
		},
		watch: {
			visible(value) {
				if (value) {
					if (this.skipNextInit) {
						this.skipNextInit = false;
						return;
					}
					this.initForm();
				}
			},
		},
		beforeUnmount() {
			revokeLocalFiles(this.form.items);
		},
		methods: {
			getFileIconMark(item) {
				const filename = item?.filename || '';
				if (item?.previewUrl && /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(filename)) {
					return '图片';
				}
				return getPayFileUnlockFileIconMark(filename);
			},
			getFileIconUrl(item) {
				const filename = item?.filename || '';
				if (item?.previewUrl && /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(filename)) {
					return item.previewUrl;
				}
				const key = getPayFileUnlockFileIconKey(filename);
				return LOCAL_ASSET.payFileIcons[key] || LOCAL_ASSET.payFileIcons.file;
			},
			initForm() {
				const source = normalizeHeaderFiles(this.value);
				const incoming = normalizeLocalFiles(source.items);
				const incomingIds = new Set(incoming.map((item) => item.id));
				this.form.items.forEach((item) => {
					if (!incomingIds.has(item.id)) {
						revokeLocalFileItem(item);
					}
				});
				this.form = {
					items: incoming,
					downloadSettings: { ...source.downloadSettings },
				};
			},
			appendFiles(files) {
				const remain = MAX_LOCAL_FILES - this.form.items.length;
				if (remain <= 0) return;
				const validFiles = filterValidLocalFileList(files, remain);
				if (!validFiles.length) return;
				return this.uploadFiles(validFiles, null, { mode: 'append' });
			},
			createUploadPlaceholder(files = []) {
				return {
					id: `pending-${uuidv4()}`,
					filename: files.length > 1 ? `${files[0].name} 等${files.length}个` : (files[0]?.name || ''),
					filesize: '',
					previewUrl: '',
					uploading: true,
					local: true,
				};
			},
			isActiveUploadItem(item, index) {
				if (!this.isUploading || item.uploading !== true) {
					return false;
				}
				if (this.uploadMode === 'append') {
					return item.id === this.uploadTargetId;
				}
				if (this.uploadMode === 'replace') {
					return index === this.replaceUploadIndex;
				}
				return false;
			},
			removeUploadPlaceholder() {
				const index = this.form.items.findIndex((item) => item.id === this.uploadTargetId);
				if (index !== -1) {
					this.form.items.splice(index, 1);
				}
			},
			clearUploadState() {
				this.isUploading = false;
				this.uploadTasks = [];
				this.uploadMode = 'initial';
				this.uploadTargetId = '';
				this.replaceUploadIndex = -1;
			},
			async uploadFiles(files, onSuccess, { mode = 'initial', replaceIndex = -1 } = {}) {
				this.isUploading = true;
				this.uploadMode = mode;
				this.uploadTasks = createUploadTasks(files);

				if (mode === 'append') {
					const placeholder = this.createUploadPlaceholder(files);
					this.uploadTargetId = placeholder.id;
					this.form.items.push(placeholder);
				} else if (mode === 'replace') {
					this.replaceUploadIndex = replaceIndex;
					this.form.items[replaceIndex].uploading = true;
				}

				try {
					const items = await uploadHeaderMediaFiles(this, {
						type: 'file',
						files,
						onProgress: (payload) => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (mode === 'append') {
						this.removeUploadPlaceholder();
						this.form.items.push(...items);
					} else if (mode === 'replace') {
						const old = this.form.items[replaceIndex];
						revokeLocalFileItem(old);
						this.form.items.splice(replaceIndex, 1, items[0]);
					} else {
						onSuccess?.(items);
					}
				} catch (error) {
					if (mode === 'append') {
						this.removeUploadPlaceholder();
					} else if (mode === 'replace' && this.form.items[replaceIndex]) {
						this.form.items[replaceIndex].uploading = false;
					}
					this.$message?.error?.('上传失败');
				} finally {
					this.clearUploadState();
				}
			},
			onAddFiles() {
				this.$refs.fileInput.value = '';
				this.$refs.fileInput.click();
			},
			onReplaceFile(index) {
				this.replaceIndex = index;
				this.$refs.replaceInput.value = '';
				this.$refs.replaceInput.click();
			},
			onDeleteFile(index) {
				const target = this.form.items[index];
				if (target?.uploading) return;
				const [removed] = this.form.items.splice(index, 1);
				revokeLocalFileItem(removed);
			},
			onFileChange(event) {
				const files = Array.from(event.target.files || []);
				this.appendFiles(files);
				event.target.value = '';
			},
			onReplaceFileChange(event) {
				const file = (event.target.files || [])[0];
				event.target.value = '';
				if (!file || this.replaceIndex < 0) return;
				const valid = filterValidLocalFileList([file], 1);
				if (!valid.length) return;
				const replaceIndex = this.replaceIndex;
				this.replaceIndex = -1;
				this.uploadFiles(valid, null, { mode: 'replace', replaceIndex });
			},
			openWithFiles(files) {
				const validFiles = filterValidLocalFileList(files);
				if (!validFiles.length) return;
				revokeLocalFiles(this.form.items);
				this.form = {
					items: [],
					downloadSettings: createDefaultFileDownloadSettings(),
				};
				this.skipNextInit = true;
				this.$emit('update:visible', true);
				this.uploadFiles(validFiles, (items) => {
					this.form.items = items;
				}, { mode: 'initial' });
			},
			openWithUploadedItems(items) {
				revokeLocalFiles(this.form.items);
				this.form = {
					items: normalizeLocalFiles(items),
					downloadSettings: createDefaultFileDownloadSettings(),
				};
				this.skipNextInit = true;
				this.$emit('update:visible', true);
			},
			onDragStart(id) {
				const target = this.form.items.find((item) => item.id === id);
				if (target?.uploading) return;
				this.dragId = id;
				this.dragOverId = '';
			},
			onItemDragOver(targetId, event) {
				event.preventDefault();
				if (!this.dragId || targetId === this.dragId) return;
				this.dragOverId = targetId;
			},
			onDragEnd() {
				if (this.dragOverId && this.dragOverId !== this.dragId) {
					const items = this.form.items;
					const fromIndex = items.findIndex(item => item.id === this.dragId);
					const toIndex = items.findIndex(item => item.id === this.dragOverId);
					if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
						const [moved] = items.splice(fromIndex, 1);
						items.splice(toIndex, 0, moved);
					}
				}
				this.dragId = '';
				this.dragOverId = '';
			},
			onSortDrop(event) {
				event.preventDefault();
				this.onDragEnd();
			},
			onDownloadSettingsConfirm(downloadSettings) {
				this.form.downloadSettings = downloadSettings;
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				if (!this.uploadedItems.length) return;
				this.$emit('confirm', normalizeHeaderFiles({
					...this.form,
					items: this.uploadedItems,
				}));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.g-file-settings__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	.g-file-settings__title {
		font-size: 18px;
		font-weight: 600;
		color: #333;
	}

	.g-file-settings__close {
		font-size: 16px;
		color: #999;
		cursor: pointer;
		line-height: 1;

		&:hover {
			color: #666;
		}
	}

	.g-file-settings__list {
		border: 1px solid #eee;
		border-radius: 4px;
		overflow: hidden;
	}

	.g-file-settings__item {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 14px 16px;
		border-bottom: 1px solid #f0f0f0;
		cursor: grab;
		user-select: none;

		&.is-uploading {
			cursor: default;
			background: #fafafa;
		}

		&:last-child {
			border-bottom: none;
		}
	}

	.g-file-settings__icon {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.g-file-settings__icon-img {
		width: 48px;
		height: 48px;
		object-fit: contain;
	}

	.g-file-settings__info {
		min-width: 0;
		flex: 1;
	}

	.g-file-settings__name {
		font-size: 13px;
		color: #333;
		line-height: 1.4;
		word-break: break-all;
	}

	.g-file-settings__size {
		margin-top: 4px;
		font-size: 12px;
		color: #999;
		line-height: 1.4;
	}

	.g-file-settings__actions {
		display: flex;
		gap: 6px;
		flex-shrink: 0;
	}

	.g-file-settings__action-btn {
		color: #999;
		font-size: 16px;
		cursor: pointer;
		margin-right: 10px;
		&:hover {
			color: #285af9;
		}

		&.is-delete {
			font-size: 14px;
		}
	}

	.g-file-settings__toolbar {
		.edit-list-sort-toolbar-row();
		margin-top: 14px;
	}

	.g-file-settings__toolbar-main {
		.edit-list-sort-toolbar-main();
		justify-content: space-between;
		flex: 1;
		min-width: 0;
	}

	.g-file-settings__toolbar-left {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.g-file-settings__outline-btn {
		height: 32px;
		padding: 0 14px;
		border: 1px solid #d9d9d9;
		border-radius: 4px;
		background: #fff;
		font-size: 13px;
		color: #666;
		cursor: pointer;

		&:hover {
			border-color: #285af9;
			color: #285af9;
		}
	}

	.g-file-settings__footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		margin-top: 24px;
		padding-top: 16px;
		border-top: 1px solid #f0f0f0;
		gap: 12px;
	}

	.g-file-settings__count {
		font-size: 13px;
		color: #999;
		flex-shrink: 0;
	}

	.g-file-settings__hint {
		.edit-list-sort-hint-text();
	}

	.g-file-settings__footer-actions {
		display: flex;
		gap: 10px;
		flex-shrink: 0;
	}

	.g-file-settings__btn {
		min-width: 72px;
		height: 32px;
		padding: 0 16px;
		border-radius: 4px;
		font-size: 14px;
		cursor: pointer;
		outline: none;

		&.is-cancel {
			border: 1px solid #d9d9d9;
			background: #fff;
			color: #333;

			&:hover {
				color: #5280ff;
    			border-color: #5280ff;
			}
		}

		&.is-confirm {
			border: none;
			background: #285af9;
			color: #fff;

			&:hover:not(:disabled) {
				background: #3868ff;
			}

			&:disabled {
				background: #3868ff;
				cursor: not-allowed;
			}
		}
	}

	.g-file-settings__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-file-settings-modal {
		.edit-modal-scroll();

		.ant-modal-body {
			padding: 20px 24px 18px;
		}
	}
</style>
